import React, { Component, createRef } from "react";

// CSS
// The wizard floats on the same ground, inside the same haloed white sheet, as
// /login and /join -- so it reuses that stylesheet for the parent card and only
// adds what is particular to the conversation inside it.
import "../Join/Join.scss";
import "./Onboarding.scss";

// Components
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";

// Helper
import { apiFetch, platformInfo, isLoggedIn, getCurrentUser } from "../../Helper/Api";

// Properties
const properties = require("../../properties.json");

// The dark-ink lockup: the one in properties.teacher.logo has a white
// wordmark, which vanishes on the light surface of the thread.
const LOGO = require("../../assets/img/logo copy.png").default;

const STEPS = [
  { id: 1, title: "المراحل الدراسية" },
  { id: 2, title: "المواد" },
  { id: 3, title: "الفترات الدراسية" },
  { id: 4, title: "رابطك الخاص" },
];

const TILT = 1.6; // degrees the sheet leans towards the pointer

class Onboarding extends Component {
  constructor(props) {
    super(props);
    this.toast = createRef(null);
    this.sheet = createRef(null);
    this.thread = null;
    this.subdomainCheckTimer = null;
    this.stillPlease = false;
  }

  state = {
    loading: true,
    step: 1,
    baseDomain: "",

    schoolStages: [],
    materials: [],
    periods: [],

    newStageName: "",
    selectedStageId: null,
    newMaterialName: "",
    selectedMaterialId: null,
    newPeriodName: "",

    subdomain: "",
    subdomainAvailable: null,
    subdomainChecking: false,
    savingSubdomain: false,
    subdomainSaved: null,

    // a request is in flight: the reply being sent, and the one being withdrawn
    adding: false,
    removingId: null,
  };

  componentDidMount() {
    document.title = properties.site_name + " | تفعيل الحساب";
    platformInfo()
      .then((info) => this.setState({ baseDomain: (info && info.baseDomain) || "" }))
      .catch(() => {});
    this.stillPlease =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!isLoggedIn()) {
      window.location.href = "/";
      return;
    }
    this.loadAll();
  }

  // the newest reply should always be the one in view
  componentDidUpdate() {
    if (this.thread) this.thread.scrollTop = this.thread.scrollHeight;
  }

  loadAll = async () => {
    try {
      const user = getCurrentUser();
      const [status, schoolStages, materials, periods] = await Promise.all([
        apiFetch("/teachers/me/onboarding-status"),
        apiFetch("/lookup/school-stages/by-teacher", { query: { teacherUserId: user.userId } }),
        apiFetch("/lookup/materials", { query: { branchId: user.branchId, teacherUserId: user.userId } }),
        apiFetch("/periods"),
      ]);

      if (status.complete) {
        window.location.href = "/welcome";
        return;
      }

      let step = 1;
      if (schoolStages.length > 0) step = 2;
      if (materials.length > 0) step = 3;
      if (periods.length > 0) step = 4;

      this.setState({
        loading: false,
        step,
        schoolStages,
        materials,
        periods,
        subdomain: status.subdomain || "",
        subdomainSaved: status.subdomain || null,
        // nothing is picked for the teacher -- the picker opens empty and asks
        selectedStageId: null,
        selectedMaterialId: null,
      });
    } catch (error) {
      console.error("Error loading onboarding state:", error);
      this.setState({ loading: false });
    }
  };

  // ---- Step 1: school stages ----
  addStage = async () => {
    const name = this.state.newStageName.trim();
    if (!name || this.state.adding) return;
    this.setState({ adding: true });
    try {
      const stage = await apiFetch("/lookup/school-stages", {
        method: "POST",
        body: { name, branchId: getCurrentUser()?.branchId },
      });
      this.setState((s) => ({
        schoolStages: [...s.schoolStages, stage],
        newStageName: "",
        adding: false,
      }));
    } catch (error) {
      this.setState({ adding: false });
      this.toast.current.show({ severity: "error", summary: "تعذرت الإضافة", detail: error.message });
    }
  };

  removeStage = async (id) => {
    if (this.state.removingId) return;
    this.setState({ removingId: id });
    try {
      await apiFetch(`/lookup/school-stages/${id}`, { method: "DELETE" });
      this.setState((s) => ({ schoolStages: s.schoolStages.filter((x) => x.id !== id), removingId: null }));
    } catch (error) {
      this.setState({ removingId: null });
      this.toast.current.show({ severity: "error", summary: "تعذر الحذف", detail: error.message });
    }
  };

  // ---- Step 2: materials ----
  addMaterial = async () => {
    const name = this.state.newMaterialName.trim();
    if (!name || !this.state.selectedStageId || this.state.adding) return;
    this.setState({ adding: true });
    try {
      const material = await apiFetch("/lookup/materials", {
        method: "POST",
        body: { name, stageId: this.state.selectedStageId, branchId: getCurrentUser()?.branchId },
      });
      this.setState((s) => ({
        materials: [...s.materials, material],
        newMaterialName: "",
        adding: false,
      }));
    } catch (error) {
      this.setState({ adding: false });
      this.toast.current.show({ severity: "error", summary: "تعذرت الإضافة", detail: error.message });
    }
  };

  removeMaterial = async (id) => {
    if (this.state.removingId) return;
    this.setState({ removingId: id });
    try {
      await apiFetch(`/lookup/materials/${id}`, { method: "DELETE" });
      this.setState((s) => ({ materials: s.materials.filter((x) => x.id !== id), removingId: null }));
    } catch (error) {
      this.setState({ removingId: null });
      this.toast.current.show({ severity: "error", summary: "تعذر الحذف", detail: error.message });
    }
  };

  // ---- Step 3: periods ----
  addPeriod = async () => {
    const name = this.state.newPeriodName.trim();
    if (!name || !this.state.selectedMaterialId || this.state.adding) return;
    this.setState({ adding: true });
    try {
      const period = await apiFetch("/periods", {
        method: "POST",
        body: { name, branchId: getCurrentUser()?.branchId, materialId: this.state.selectedMaterialId },
      });
      this.setState((s) => ({ periods: [...s.periods, period], newPeriodName: "", adding: false }));
    } catch (error) {
      this.setState({ adding: false });
      this.toast.current.show({ severity: "error", summary: "تعذرت الإضافة", detail: error.message });
    }
  };

  removePeriod = async (id) => {
    if (this.state.removingId) return;
    this.setState({ removingId: id });
    try {
      await apiFetch(`/periods/${id}`, { method: "DELETE" });
      this.setState((s) => ({ periods: s.periods.filter((x) => x.id !== id), removingId: null }));
    } catch (error) {
      this.setState({ removingId: null });
      this.toast.current.show({ severity: "error", summary: "تعذر الحذف", detail: error.message });
    }
  };

  // ---- Step 4: subdomain ----
  onSubdomainChange = (value) => {
    const clean = value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    this.setState({ subdomain: clean, subdomainAvailable: null });
    clearTimeout(this.subdomainCheckTimer);
    if (clean.length < 3) return;
    this.subdomainCheckTimer = setTimeout(async () => {
      this.setState({ subdomainChecking: true });
      try {
        const res = await apiFetch("/teachers/subdomain-available", { query: { value: clean } });
        this.setState({ subdomainAvailable: res.available, subdomainChecking: false });
      } catch (error) {
        this.setState({ subdomainChecking: false });
      }
    }, 400);
  };

  saveSubdomain = async () => {
    const { subdomain } = this.state;
    if (!subdomain || subdomain.length < 3) {
      this.toast.current.show({ severity: "error", summary: "رابط غير صالح", detail: "3 أحرف على الأقل" });
      return;
    }
    this.setState({ savingSubdomain: true });
    try {
      await apiFetch("/teachers/me/subdomain", { method: "PUT", body: { subdomain } });
      this.toast.current.show({ severity: "success", summary: "تم بنجاح", detail: "تم تفعيل حسابك بالكامل!" });
      setTimeout(function () {
        window.location.href = `/welcome`;
      }, 1000);
    } catch (error) {
      this.setState({ savingSubdomain: false });
      this.toast.current.show({ severity: "error", summary: "تعذر الحفظ", detail: error.message });
    }
  };

  goToStep = (step) => this.setState({ step });

  // ---- the sheet leans towards the pointer, as it does on /login ----
  handleTilt = (e) => {
    if (this.stillPlease || !this.sheet.current || e.pointerType === "touch") return;
    const el = this.sheet.current;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `rotateY(${(px * TILT).toFixed(2)}deg) rotateX(${(-py * TILT).toFixed(2)}deg)`;
  };

  resetTilt = () => {
    if (this.sheet.current) this.sheet.current.style.transform = "";
  };

  render() {
    const { loading, step } = this.state;

    if (loading) {
      return (
        <div className="join-page onboarding-page loading">
          <div className="ob-loader" role="status">
            <span className="ring"></span>
            <b>...جاري التحميل</b>
          </div>
        </div>
      );
    }

    const current = STEPS[step - 1];

    return (
      <div className="join-page onboarding-page">
        <Toast ref={this.toast}></Toast>

        <div
          className="sheet-wrap"
          onPointerMove={this.handleTilt}
          onPointerLeave={this.resetTilt}
        >
          {/* the parent card is untouched; its whole inside is the conversation */}
          <div className="sheet ob-chat" ref={this.sheet}>

            <div className="ob-bar">
              <span
                className="av"
                role="img"
                aria-label={properties.site_name}
                style={{ backgroundImage: `url(${LOGO})` }}
              ></span>
              <span className="who">
                <b>تفعيل حسابك</b>
                <small>الخطوة {step} من {STEPS.length} · {current.title}</small>
              </span>
              <span className="dots" aria-hidden="true">
                {STEPS.map((s) => (
                  <i
                    key={s.id}
                    className={`${step > s.id ? "done" : ""} ${step === s.id ? "now" : ""}`}
                    title={s.title}
                    onClick={() => (step > s.id ? this.goToStep(s.id) : null)}
                  ></i>
                ))}
              </span>
            </div>

            {step === 1 && this.renderStep1()}
            {step === 2 && this.renderStep2()}
            {step === 3 && this.renderStep3()}
            {step === 4 && this.renderStep4()}

          </div>
        </div>
      </div>
    );
  }

  // The picker rides along with the conversation: it is shown again after every
  // answer, so the stage (or material) the next answer will belong to is always
  // the last thing before the composer. Every copy is the same one control --
  // same value, same setter -- so switching it anywhere switches the current
  // one, which is what the label says it is.
  renderStagePicker = (options, key) => {
    const chosen = this.state.selectedStageId != null;
    return (
      <div
        className={`bubble sys pick ${chosen ? "" : "needs"}`}
        key={`stage-${key}`}
        style={{ "--d": "120ms" }}
      >
        <span className="pick-label">{chosen ? "المرحلة الحالية" : "اختر المرحلة الأول"}</span>
        <Dropdown
          className="ob-select"
          value={this.state.selectedStageId}
          options={options}
          onChange={(e) => this.setState({ selectedStageId: e.value })}
          placeholder="اختر المرحلة الدراسية"
        />
        {!chosen && (
          <span className="pick-hint">لازم تختار المرحلة عشان المادة تتسجّل تحتها</span>
        )}
      </div>
    );
  };

  renderMaterialPicker = (options, key) => {
    const chosen = this.state.selectedMaterialId != null;
    return (
      <div
        className={`bubble sys pick ${chosen ? "" : "needs"}`}
        key={`material-${key}`}
        style={{ "--d": "120ms" }}
      >
        <span className="pick-label">{chosen ? "المادة الحالية" : "اختر المادة الأول"}</span>
        <Dropdown
          className="ob-select"
          value={this.state.selectedMaterialId}
          options={options}
          onChange={(e) => this.setState({ selectedMaterialId: e.value })}
          placeholder="اختر المادة"
        />
        {!chosen && (
          <span className="pick-hint">لازم تختار المادة عشان الفترة تتسجّل تحتها</span>
        )}
      </div>
    );
  };

  // every step is the same shape: the platform asks, the teacher answers in the
  // composer, and each answer stays in the thread as its own reply
  renderThread = (children) => (
    <div className="ob-thread" ref={(el) => (this.thread = el)}>
      {/* a refusal from the server stays a toast; the thread holds only the
          conversation itself */}
      <div className="ob-stream">{children}</div>
    </div>
  );

  renderStep1() {
    const { schoolStages, newStageName, adding, removingId } = this.state;
    return (
      <>
        {this.renderThread(
          <>
            <div className="bubble sys" style={{ "--d": "60ms" }}>
              <b>أضف المراحل الدراسية اللي بتدرّسها</b>
              <p>مثال: الصف الأول الثانوي، الصف الثاني الإعدادي...</p>
            </div>

            {schoolStages.map((s, i) => (
              <div
                className={`bubble me ${removingId === s.id ? "going" : ""}`}
                key={s.id}
                style={{ "--d": `${120 + i * 60}ms` }}
              >
                <span className="tick">✓</span>
                <span className="txt">{s.name}</span>
                {removingId === s.id ? (
                  <i className="pi pi-spin pi-spinner"></i>
                ) : (
                  <i className="pi pi-times" onClick={() => this.removeStage(s.id)}></i>
                )}
              </div>
            ))}

            {/* the reply is already in the thread while the server is still
                saving it, greyed out with a spinner where the tick will go */}
            {adding && newStageName.trim() && (
              <div className="bubble me pending" style={{ "--d": "0ms" }}>
                <span className="typing" aria-hidden="true"><i></i><i></i><i></i></span>
                <span className="txt">{newStageName.trim()}</span>
              </div>
            )}

            {schoolStages.length === 0 && !adding && (
              <div className="bubble sys muted" style={{ "--d": "160ms" }}>
                لسه مفيش مراحل دراسية مضافة
              </div>
            )}

            {schoolStages.length > 0 && (
              <div className="bubble sys done" style={{ "--d": "200ms" }}>
                تمام، {schoolStages.length} مراحل اتسجّلت. ضيف كمان أو كمّل للمواد.
              </div>
            )}
          </>
        )}

        <div className="ob-composer">
          <div className="ob-write">
            <InputText
              autoFocus
              placeholder="اسم المرحلة الدراسية"
              value={newStageName}
              onChange={(e) => this.setState({ newStageName: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && this.addStage()}
            />
            <Button
              className="send"
              icon={adding ? "pi pi-spin pi-spinner" : "pi pi-arrow-up"}
              aria-label="إضافة"
              onClick={this.addStage}
              disabled={adding || !newStageName.trim()}
            />
          </div>
          <Button
            className="next-button"
            label="كمّل للمواد"
            icon="pi pi-arrow-left"
            iconPos="left"
            disabled={schoolStages.length === 0}
            onClick={() => this.goToStep(2)}
          />
        </div>
      </>
    );
  }

  renderStep2() {
    const { schoolStages, materials, selectedStageId, newMaterialName, adding, removingId } =
      this.state;
    const stageOptions = schoolStages.map((s) => ({ label: s.name, value: s.id }));
    return (
      <>
        {this.renderThread(
          <>
            <div className="bubble sys" style={{ "--d": "60ms" }}>
              <b>أضف المواد لكل مرحلة</b>
              <p>اختر المرحلة الدراسية، وسمّي المادة اللي هتدرّسها فيها</p>
            </div>

            {/* the picker always leads: it is the context the answer under it
                was given in, and the last one is the context for the next */}
            {materials.map((m, i) => {
              const stage = schoolStages.find((s) => s.id === m.stageId);
              return (
                <React.Fragment key={m.id}>
                  {this.renderStagePicker(stageOptions, `before-${m.id}`)}
                  <div
                    className={`bubble me ${removingId === m.id ? "going" : ""}`}
                    style={{ "--d": `${180 + i * 60}ms` }}
                  >
                    <span className="tick">✓</span>
                    <span className="txt">
                      {m.name} <small>({stage ? stage.name : ""})</small>
                    </span>
                    {removingId === m.id ? (
                      <i className="pi pi-spin pi-spinner"></i>
                    ) : (
                      <i className="pi pi-times" onClick={() => this.removeMaterial(m.id)}></i>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            {this.renderStagePicker(stageOptions, "next")}

            {adding && newMaterialName.trim() && (
              <div className="bubble me pending" style={{ "--d": "0ms" }}>
                <span className="typing" aria-hidden="true"><i></i><i></i><i></i></span>
                <span className="txt">{newMaterialName.trim()}</span>
              </div>
            )}

            {materials.length === 0 && !adding && (
              <div className="bubble sys muted" style={{ "--d": "200ms" }}>
                لسه مفيش مواد مضافة
              </div>
            )}

            {materials.length > 0 && (
              <div className="bubble sys done" style={{ "--d": "240ms" }}>
                تمام، {materials.length} مواد اتسجّلت. ضيف كمان أو كمّل للفترات.
              </div>
            )}
          </>
        )}

        <div className="ob-composer">
          <Button
            className="back-button p-button-text"
            icon="pi pi-arrow-right"
            label="السابق"
            onClick={() => this.goToStep(1)}
          />
          <div className="ob-write">
            <InputText
              autoFocus
              placeholder="اسم المادة"
              value={newMaterialName}
              onChange={(e) => this.setState({ newMaterialName: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && this.addMaterial()}
            />
            <Button
              className="send"
              icon={adding ? "pi pi-spin pi-spinner" : "pi pi-arrow-up"}
              aria-label="إضافة"
              onClick={this.addMaterial}
              disabled={adding || !selectedStageId || !newMaterialName.trim()}
            />
          </div>
          <Button
            className="next-button"
            label="كمّل للفترات"
            icon="pi pi-arrow-left"
            iconPos="left"
            disabled={materials.length === 0}
            onClick={() => this.goToStep(3)}
          />
        </div>
      </>
    );
  }

  renderStep3() {
    const { materials, periods, selectedMaterialId, newPeriodName, adding, removingId } =
      this.state;
    const materialOptions = materials.map((m) => ({ label: m.name, value: m.id }));
    return (
      <>
        {this.renderThread(
          <>
            <div className="bubble sys" style={{ "--d": "60ms" }}>
              <b>أنشئ الفترات الدراسية</b>
              <p>الفترة هي اللي هيشترك فيها الطالب لمادة معيّنة (زي "الترم الأول")</p>
            </div>

            {periods.map((p, i) => (
              <React.Fragment key={p.id}>
                {this.renderMaterialPicker(materialOptions, `before-${p.id}`)}
                <div
                  className={`bubble me ${removingId === p.id ? "going" : ""}`}
                  style={{ "--d": `${180 + i * 60}ms` }}
                >
                  <span className="tick">✓</span>
                  <span className="txt">{p.name}</span>
                  {removingId === p.id ? (
                    <i className="pi pi-spin pi-spinner"></i>
                  ) : (
                    <i className="pi pi-times" onClick={() => this.removePeriod(p.id)}></i>
                  )}
                </div>
              </React.Fragment>
            ))}

            {this.renderMaterialPicker(materialOptions, "next")}

            {adding && newPeriodName.trim() && (
              <div className="bubble me pending" style={{ "--d": "0ms" }}>
                <span className="typing" aria-hidden="true"><i></i><i></i><i></i></span>
                <span className="txt">{newPeriodName.trim()}</span>
              </div>
            )}

            {periods.length === 0 && !adding && (
              <div className="bubble sys muted" style={{ "--d": "200ms" }}>
                لسه مفيش فترات دراسية مضافة
              </div>
            )}

            {periods.length > 0 && (
              <div className="bubble sys done" style={{ "--d": "240ms" }}>
                تمام، {periods.length} فترات اتسجّلت. فاضل خطوة واحدة بس.
              </div>
            )}
          </>
        )}

        <div className="ob-composer">
          <Button
            className="back-button p-button-text"
            icon="pi pi-arrow-right"
            label="السابق"
            onClick={() => this.goToStep(2)}
          />
          <div className="ob-write">
            <InputText
              autoFocus
              placeholder="اسم الفترة الدراسية"
              value={newPeriodName}
              onChange={(e) => this.setState({ newPeriodName: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && this.addPeriod()}
            />
            <Button
              className="send"
              icon={adding ? "pi pi-spin pi-spinner" : "pi pi-arrow-up"}
              aria-label="إضافة"
              onClick={this.addPeriod}
              disabled={adding || !selectedMaterialId || !newPeriodName.trim()}
            />
          </div>
          <Button
            className="next-button"
            label="كمّل للرابط"
            icon="pi pi-arrow-left"
            iconPos="left"
            disabled={periods.length === 0}
            onClick={() => this.goToStep(4)}
          />
        </div>
      </>
    );
  }

  renderStep4() {
    const { subdomain, subdomainAvailable, subdomainChecking, savingSubdomain } = this.state;
    return (
      <>
        {this.renderThread(
          <>
            <div className="bubble sys" style={{ "--d": "60ms" }}>
              <b>اختر رابطك الخاص</b>
              <p>
                ده الرابط اللي طلابك هيدخلوا بيه على صفحتك الخاصة. اختر اسم مميز ومش
                هيتكرر مع حد تاني.
              </p>
            </div>

            {subdomain.length > 0 && (
              <div className="bubble me link" style={{ "--d": "120ms" }}>
                <span className="txt" dir="ltr">{subdomain}.{this.state.baseDomain || "igma-it.com"}</span>
              </div>
            )}

            <div
              className={`bubble sys state ${
                subdomainAvailable === true ? "ok" : subdomainAvailable === false ? "bad" : ""
              }`}
              style={{ "--d": "160ms" }}
            >
              {subdomainChecking && (
                <span className="checking"><i className="pi pi-spin pi-spinner"></i>...جاري التأكد من الإتاحة</span>
              )}
              {!subdomainChecking && subdomainAvailable === true && (
                <span className="available">✓ متاح — تقدر تفعّل حسابك دلوقتي</span>
              )}
              {!subdomainChecking && subdomainAvailable === false && (
                <span className="unavailable">✗ محجوز، جرّب اسم تاني</span>
              )}
              {!subdomainChecking && subdomainAvailable === null && (
                <span className="idle">اكتب الاسم بالإنجليزي، 3 أحرف على الأقل.</span>
              )}
            </div>
          </>
        )}

        <div className="ob-composer">
          <Button
            className="back-button p-button-text"
            icon="pi pi-arrow-right"
            label="السابق"
            onClick={() => this.goToStep(3)}
          />
          <div
            className={`ob-write domain ${
              subdomainAvailable === true ? "ok" : subdomainAvailable === false ? "bad" : ""
            }`}
          >
            <InputText
              autoFocus
              value={subdomain}
              placeholder="ahmed-math"
              onChange={(e) => this.onSubdomainChange(e.target.value)}
            />
            <span className="suffix">.{this.state.baseDomain || "igma-it.com"}</span>
          </div>
          <Button
            className="next-button activate"
            label={savingSubdomain ? "جاري التفعيل..." : "تفعيل الحساب"}
            icon={savingSubdomain ? "ob-spin" : "pi pi-check"}
            iconPos="left"
            disabled={savingSubdomain || subdomain.length < 3 || subdomainAvailable === false}
            onClick={this.saveSubdomain}
          />
        </div>
      </>
    );
  }
}

export default Onboarding;
